import {UserState} from './UserStore.types';

import {injected} from 'brandi';

import {
  HttpError,
  ProjectUserAuthState,
  ProjectUserAuthStateError,
  ProjectUserAuthStateLoading,
  ProjectUserAuthStateLoggedIn,
  ProjectUserAuthStatus,
} from '@specials/project-user';

import {LocationType, ProjectUserInfoData, ProjectUserLoginData} from '@/services/ProjectAuthApi/types';
import {Store} from '@/stores/Store';

export class UserStore extends Store<ProjectUserAuthState<ProjectUserInfoData, ProjectUserLoginData>> {
  constructor() {
    super({status: ProjectUserAuthStatus.Loading, error: undefined, data: undefined});
  }

  public isLoggedIn = (): boolean => {
    return this.getState().status === ProjectUserAuthStatus.LoggedIn;
  };

  public isError = (): boolean => {
    return this.getState().status === ProjectUserAuthStatus.Error;
  };

  public isLoading = (): boolean => {
    return this.getState().status === ProjectUserAuthStatus.Loading;
  };

  public static isUserStateLoggedIn(
    userState: UserState,
  ): userState is ProjectUserAuthStateLoggedIn<ProjectUserInfoData, ProjectUserLoginData> {
    return userState.status === ProjectUserAuthStatus.LoggedIn;
  }

  public static isUserStateLoading(userState: UserState): userState is ProjectUserAuthStateLoading {
    return userState.status === ProjectUserAuthStatus.Loading;
  }

  public static isUserStateError(userState: UserState): userState is ProjectUserAuthStateError {
    return userState.status === ProjectUserAuthStatus.Error;
  }

  public isGuest = (): boolean => {
    return this.getState().status === ProjectUserAuthStatus.Guest;
  };

  public getErrorData = (): HttpError => {
    if (!this.isError()) {
      throw new Error('Нет доступа к данным об ошибке');
    }
    const state = this.getState();
    return state.error as HttpError;
  };

  public getUserId = (): string | undefined => {
    return this.getState().data?.userInfo.user.id;
  };

  public getLocationType(): LocationType {
    return this.state.data?.userInfo.game.location?.type || LocationType.Location1;
  }

  public getIsDailyRewardAvailable(): boolean {
    return this.state.data?.userInfo.dailyRewards.availableToday || false;
  }

  public isDailyCompleted(): boolean {
    return !!this.state.data?.userInfo.game.isDailyCompleted;
  }

  public isOnboardingGiftAvailable(): boolean {
    return !!this.state.data?.userInfo.onboarding.onboardingGiftIsAvailable;
  }

  public getOnboardingStatus(): string {
    return this.state.data?.userInfo.onboarding.projectOnboardingStatus || '';
  }

  public getSettings(): ProjectUserInfoData['settings'] | null {
    return this.state.data?.userInfo.settings || null;
  }

  public setLocationType(type: LocationType): void {
    if (UserStore.isUserStateLoggedIn(this.state)) {
      this.setState({
        ...this.state,
        data: {
          ...this.state.data,
          userInfo: {
            ...this.state.data.userInfo,
            game: {
              ...this.state.data.userInfo.game,
              location: {
                type,
              },
            },
          },
        },
      });
    }
  }

  public setIsAvailableDailyReward(isAvailable: boolean): void {
    if (UserStore.isUserStateLoggedIn(this.state)) {
      this.setState({
        ...this.state,
        data: {
          ...this.state.data,
          userInfo: {
            ...this.state.data.userInfo,
            dailyRewards: {
              availableToday: isAvailable,
            },
          },
        },
      });
    }
  }

  public setOnboardingGiftClaimed(): void {
    if (UserStore.isUserStateLoggedIn(this.state)) {
      this.setState({
        ...this.state,
        data: {
          ...this.state.data,
          userInfo: {
            ...this.state.data.userInfo,
            onboarding: {
              ...this.state.data.userInfo.onboarding,
              onboardingGiftIsAvailable: false,
            },
          },
        },
      });
    }
  }

  public completeDailyGame(): void {
    if (!this.state.data) return;

    this.setState({
      ...this.state,
      data: {
        ...this.state.data,
        userInfo: {
          ...this.state.data.userInfo,
          game: {
            ...this.state.data.userInfo.game,

            isDailyCompleted: true,
          },
        },
      },
    });
  }
}

injected(UserStore);
